import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const STATUSES = ['pending', 'approved', 'rejected'];
const CATEGORIES = ['Travel', 'Office Supplies', 'Utilities', 'Rent', 'Salaries', 'Marketing', 'Maintenance', 'Other'];
const INIT = { title: '', category: '', amount: '', date: new Date().toISOString().slice(0, 10), status: 'pending', notes: '' };
const LIMIT = 15;

function fmtMoney(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function Expenses({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [status, setStatus]     = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading]   = useState(false);
  const [summary, setSummary]   = useState(null);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);

  useEffect(() => { setTitle('Expenses'); }, [setTitle]);

  const loadSummary = () => {
    api.get('/expenses/summary').then(r => setSummary(r.data || null)).catch(() => {});
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/expenses?status=${status}&category=${encodeURIComponent(category)}&page=${page}&limit=${LIMIT}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [status, category, page]);

  useEffect(() => { load(); loadSummary(); }, [load]);

  const openAdd  = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => {
    setForm({ title: item.title, category: item.category || '', amount: item.amount ?? '', date: item.date ? item.date.slice(0, 10) : '', status: item.status, notes: item.notes || '' });
    setEditId(item.id); setModal(true);
  };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) return alert('Title is required');
    if (!form.amount) return alert('Amount is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/expenses/${editId}`, form);
      else        await api.post('/expenses', form);
      closeModal(); load(); loadSummary();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving expense');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete expense "${title}"?`)) return;
    try { await api.delete(`/expenses/${id}`); load(); loadSummary(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const handleApprove = async (id) => {
    try { await api.put(`/expenses/${id}`, { status: 'approved' }); load(); loadSummary(); }
    catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const handleReject = async (id) => {
    try { await api.put(`/expenses/${id}`, { status: 'rejected' }); load(); loadSummary(); }
    catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Expenses</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Expense
        </button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { label: 'Total', value: fmtMoney(summary.total) },
            { label: 'Approved', value: fmtMoney(summary.approved) },
            { label: 'Pending', value: fmtMoney(summary.pending) },
            { label: 'Rejected', value: fmtMoney(summary.rejected) },
          ].map(s => (
            <div className="stat-card" key={s.label}>
              <div className="label">{s.label}</div>
              <div className="value" style={{ fontSize: 20 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="toolbar">
          <select className="form-control" style={{ width: 140 }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <select className="form-control" style={{ width: 170 }} value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} expenses</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="text-right">Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="empty">No expenses found</td></tr>
                ) : items.map(item => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.title}</strong>
                      {item.notes && <div className="text-muted" style={{ fontSize: 11 }}>{item.notes.slice(0, 60)}{item.notes.length > 60 ? '…' : ''}</div>}
                    </td>
                    <td>{item.category || '—'}</td>
                    <td className="text-muted">{item.date ? item.date.slice(0, 10) : '—'}</td>
                    <td><span className={`badge badge-${item.status}`}>{item.status}</span></td>
                    <td className="text-right"><strong>{fmtMoney(item.amount)}</strong></td>
                    <td>
                      <div className="actions">
                        {item.status === 'pending' && (
                          <>
                            <button className="btn-icon success" title="Approve" onClick={() => handleApprove(item.id)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                            </button>
                            <button className="btn-icon danger" title="Reject" onClick={() => handleReject(item.id)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </>
                        )}
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.title)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Expense' : 'Add Expense'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Title *</label>
                  <input className="form-control" value={form.title} onChange={e => f('title', e.target.value)} placeholder="Expense description" />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select className="form-control" value={form.category} onChange={e => f('category', e.target.value)}>
                    <option value="">Select category…</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input type="number" className="form-control" value={form.amount} onChange={e => f('amount', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" className="form-control" value={form.date} onChange={e => f('date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Optional notes…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Expense' : 'Add Expense'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
